import type {
  ExpenseInconsistencyItem,
  InconsistencyMatchingResult,
} from 'src/core/modules/verification-of-payee/models';
import { isVerificationOfPayeeResultInvalid } from 'src/core/modules/verification-of-payee/utils';

import type { UserScheduledPayments } from '../types';
import { isUserVerificationOfPayeeCompatible } from './isUserVerificationOfPayeeCompatible';

export type VerificationOfPayeeUserInfo = {
  userId: string;
  iban: string;
  accountHolderName: string;
  userName: string;
  expenseCount: number;
};

export const extractVerificationOfPayeeUserInfo = (
  usersScheduledPayments: UserScheduledPayments[],
  companyCurrency: string,
): VerificationOfPayeeUserInfo[] => {
  const result: VerificationOfPayeeUserInfo[] = [];

  for (const userScheduledPayments of usersScheduledPayments) {
    const { user, scheduledPayments } = userScheduledPayments;

    if (
      !isUserVerificationOfPayeeCompatible(
        userScheduledPayments,
        companyCurrency,
      )
    ) {
      continue;
    }

    const bankAddress = user.bankInfo?.bankAddress;
    if (!bankAddress) {
      continue;
    }

    let iban: string | undefined;
    let accountHolderName: string | undefined;

    if (
      bankAddress.type === 'IBAN+BIC' ||
      bankAddress.type === 'IBAN+BIC+CUC'
    ) {
      iban = bankAddress.info.iban;
      accountHolderName = bankAddress.info.accountHolderName;
    }

    if (!iban || !accountHolderName) {
      continue;
    }

    const expenseCount = scheduledPayments.reduce(
      (count, sp) => count + sp.requests.length,
      0,
    );

    result.push({
      userId: user.id,
      iban,
      accountHolderName,
      userName: `${user.firstName} ${user.lastName}`,
      expenseCount,
    });
  }

  return result;
};

export const filterUsersWithInvalidInitialVerificationOfPayee = (
  usersScheduledPayments: UserScheduledPayments[],
): UserScheduledPayments[] => {
  return usersScheduledPayments.filter((userScheduledPayments) => {
    const verificationOfPayee = userScheduledPayments.verificationOfPayee;
    return (
      verificationOfPayee &&
      isVerificationOfPayeeResultInvalid(
        verificationOfPayee.initialResult.matchingResult,
      ) &&
      !verificationOfPayee.isIgnored
    );
  });
};

export const filterUsersWithInvalidVerificationOfPayee = (
  usersScheduledPayments: UserScheduledPayments[],
): UserScheduledPayments[] => {
  return usersScheduledPayments.filter((userScheduledPayments) => {
    const vop = userScheduledPayments.verificationOfPayee;
    const latestResult = vop?.updatedResult ?? vop?.initialResult;

    return (
      vop &&
      latestResult &&
      isVerificationOfPayeeResultInvalid(latestResult.matchingResult)
    );
  });
};

const mapVerificationOfPayeeUsersToInconsistencyItems = (
  usersInfo: VerificationOfPayeeUserInfo[],
  getMatchingResult: (userId: string) => InconsistencyMatchingResult,
): ExpenseInconsistencyItem[] =>
  usersInfo.map((userInfo) => ({
    id: userInfo.userId,
    userName: userInfo.userName,
    expenseCount: userInfo.expenseCount,
    matchingResult: getMatchingResult(userInfo.userId),
  }));

export const mapUsersWithInvalidVerificationOfPayeeToInconsistencyItems = (
  usersWithInvalidVerificationOfPayee: UserScheduledPayments[],
  companyCurrency: string,
): ExpenseInconsistencyItem[] => {
  const usersInfo = extractVerificationOfPayeeUserInfo(
    usersWithInvalidVerificationOfPayee,
    companyCurrency,
  );

  return mapVerificationOfPayeeUsersToInconsistencyItems(
    usersInfo,
    (userId) => {
      const userScheduledPayments = usersWithInvalidVerificationOfPayee.find(
        (usp) => usp.user.id === userId,
      );

      const matchingResult =
        userScheduledPayments?.verificationOfPayee?.initialResult
          .matchingResult;
      if (!matchingResult || matchingResult === 'match') {
        throw new Error(
          'Verification of payee matching result should be defined here',
        );
      }
      return matchingResult;
    },
  );
};
